import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Calendar, Tag, Share2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import axios from "axios";
import ReactMarkdown from "react-markdown";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categoryLabels = {
  "seguro-auto": "Seguro Auto",
  "seguro-empresarial": "Seguro Empresarial",
  "seguro-vida": "Seguro de Vida",
  "seguro-residencial": "Seguro Residencial",
  "seguro-viagem": "Seguro Viagem",
  "seguro-cyber": "Riscos Cibernéticos",
  "geral": "Dicas de Seguros",
};

export default function BlogPost() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/blog/${slug}`).then((res) => setArticle(res.data)).catch(() => {}).finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <div className="pt-28 pb-16 max-w-3xl mx-auto px-4">
        <div className="animate-pulse space-y-4">
          <div className="h-4 bg-slate-200 rounded w-32" />
          <div className="h-8 bg-slate-200 rounded w-full" />
          <div className="h-8 bg-slate-200 rounded w-3/4" />
          <div className="h-4 bg-slate-100 rounded w-full mt-8" />
          <div className="h-4 bg-slate-100 rounded w-5/6" />
          <div className="h-4 bg-slate-100 rounded w-full" />
        </div>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="pt-28 pb-16 text-center">
        <h1 className="text-2xl font-heading font-bold text-navy mb-4">Artigo não encontrado</h1>
        <Link to="/blog"><Button variant="outline">Voltar ao Blog</Button></Link>
      </div>
    );
  }

  return (
    <div data-testid="blog-post-page">
      <section className="pt-20 bg-navy">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
          <Link to="/blog" className="inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-white mb-6 transition-colors" data-testid="blog-back-link">
            <ArrowLeft className="h-4 w-4" /> Voltar ao Blog
          </Link>
          <div className="flex items-center gap-3 mb-4">
            <span className="text-xs font-semibold bg-gold/20 text-gold px-2.5 py-0.5 rounded-full uppercase">{categoryLabels[article.category] || article.category}</span>
            <span className="flex items-center gap-1 text-xs text-slate-400"><Calendar className="h-3 w-3" /> {new Date(article.created_at).toLocaleDateString("pt-BR")}</span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-heading font-bold text-white leading-tight">{article.title}</h1>
        </div>
      </section>

      <section className="py-12 md:py-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-slate max-w-none">
            {article.image_url && (
              <img
                src={article.image_url.startsWith("http") ? article.image_url : `${process.env.REACT_APP_BACKEND_URL}${article.image_url}`}
                alt={article.title}
                className="w-full h-64 md:h-80 object-cover rounded-xl mb-8"
              />
            )}
            <ReactMarkdown
              components={{
                h2: ({children}) => <h2 className="text-xl font-heading font-bold text-navy mt-8 mb-3">{children}</h2>,
                h3: ({children}) => <h3 className="text-lg font-heading font-semibold text-navy mt-6 mb-2">{children}</h3>,
                p: ({children}) => <p className="text-base text-slate-700 leading-relaxed mb-4">{children}</p>,
                strong: ({children}) => <strong className="font-semibold text-navy">{children}</strong>,
                ul: ({children}) => <ul className="list-disc pl-6 mb-4 space-y-1">{children}</ul>,
                ol: ({children}) => <ol className="list-decimal pl-6 mb-4 space-y-1">{children}</ol>,
                li: ({children}) => <li className="text-base text-slate-700">{children}</li>,
              }}
            >
              {article.content}
            </ReactMarkdown>
          </div>

          <div className="mt-12 pt-8 border-t flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Tag className="h-4 w-4 text-slate-400" />
              <span className="text-sm text-slate-500">{categoryLabels[article.category] || article.category}</span>
            </div>
            <Button variant="outline" size="sm" onClick={() => { navigator.clipboard.writeText(window.location.href); }} data-testid="share-btn">
              <Share2 className="h-3.5 w-3.5 mr-1.5" /> Compartilhar
            </Button>
          </div>

          {/* CTA */}
          <div className="mt-12 bg-navy rounded-2xl p-8 text-center">
            <h3 className="font-heading font-bold text-white text-xl mb-3">Precisa de um seguro?</h3>
            <p className="text-slate-300 text-sm mb-5">Solicite uma cotação gratuita e receba as melhores opções.</p>
            <Link to="/cotacao">
              <Button className="btn-gold h-11 px-8" data-testid="blogpost-cta">Solicitar Cotação</Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
